import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, MessageCircle, Users, Heart, Play } from "lucide-react";

// Landing — elegant typography. Only "Spark" wordmark is bold.
export default function Landing() {
  return (
    <div className="min-h-[100svh] flex flex-col max-w-md mx-auto px-6 pt-4 pb-4 relative bg-[#16171A] overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{ background: "radial-gradient(ellipse 60% 50% at 70% 30%, rgba(185,95,115,0.4), transparent 70%)" }}
      />

      <header className="relative flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-11 h-11 rounded-full bg-[#B95F73] flex items-center justify-center shadow-lg shadow-[#B95F73]/40">
            <Sparkles className="w-5 h-5 text-[#F0E6D2]" fill="#F0E6D2" />
          </div>
          {/* Only word that is bold. Large for brand recognition. */}
          <span className="font-display font-black text-4xl tracking-tight">Spark</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/blog" className="font-display italic text-sm text-[#C9A961]" data-testid="landing-header-blog">Blog</Link>
          <Link to="/login" className="font-display italic text-sm text-[#C9A961]">Sign in</Link>
        </div>
      </header>

      <h1 className="relative font-display text-[34px] leading-[0.95] font-light tracking-tight mb-2">
        Date like you{" "}
        <span className="font-display-italic text-[#B95F73]">chat</span>.
      </h1>

      <p className="relative font-display text-[#A0A0A8] text-[12px] leading-snug mb-2 max-w-[34ch] font-light italic">
        Modern dating, beautifully done. Swipe, spark a 1:1, and meet tonight.
      </p>

      <motion.div
        initial={{ opacity: 0, y: 20, rotate: -8 }}
        animate={{ opacity: 1, y: 0, rotate: -6 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative mx-auto mt-auto mb-2 w-[130px] rounded-[24px] bg-gradient-to-br from-[#1B1B1E] to-[#0E0F12] border-2 border-[#2A2A2F] shadow-2xl shadow-[#B95F73]/30 p-1.5 pb-4"
      >
        <div className="w-full h-[180px] rounded-[18px] overflow-hidden relative bg-gradient-to-br from-[#B95F73] via-[#8E3F50] to-[#10B981]">
          <div className="absolute top-1 left-1/2 -translate-x-1/2 w-10 h-2.5 bg-[#16171A] rounded-full" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles className="w-12 h-12 text-[#F0E6D2]/40" fill="currentColor" strokeWidth={1} />
          </div>
        </div>

        {/* Sparkle on the phone case (no text) */}
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
          <div className="w-4 h-4 rounded-full bg-gradient-to-br from-[#C9A961] to-[#8E6F3A] flex items-center justify-center shadow-md shadow-[#C9A961]/60 ring-1 ring-[#C9A961]/40">
            <Sparkles className="w-2.5 h-2.5 text-[#16171A]" fill="currentColor" />
          </div>
        </div>

        <Link to="/signup" data-testid="landing-heart-cta" aria-label="Sign up to start matching">
          <motion.div
            className="absolute -right-3 top-1/3 w-8 h-8 rounded-full bg-[#B95F73] flex items-center justify-center text-[#F0E6D2] shadow-lg shadow-[#B95F73]/50 cursor-pointer"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            whileTap={{ scale: 0.9 }}
          >
            <Heart className="w-3.5 h-3.5" fill="currentColor" />
          </motion.div>
        </Link>
      </motion.div>

      <div className="relative grid grid-cols-3 gap-1.5 mt-2 mb-2">
        <ElegantFeature to="/signup" testid="landing-feature-match" icon={<Heart className="w-3 h-3" fill="currentColor" />} color="#B95F73" title="Match" />
        <ElegantFeature to="/signup" testid="landing-feature-groups" icon={<Users className="w-3 h-3" />} color="#10B981" title="Groups" />
        <ElegantFeature to="/signup" testid="landing-feature-aichat" icon={<MessageCircle className="w-3 h-3" />} color="#C9A961" title="AI chat" />
      </div>

      <div className="relative mt-auto space-y-1.5">
        <Link
          to="/signup"
          className="block w-full bg-[#B95F73] text-[#F0E6D2] rounded-full py-3 text-center font-display text-[15px] font-normal tracking-wide active:scale-95 transition shadow-xl shadow-[#B95F73]/40"
          data-testid="landing-cta-signup"
        >
          Make an account
        </Link>
        <Link
          to="/tour-pro"
          className="block w-full bg-[#C9A961] text-[#16171A] rounded-full py-3 text-center font-display text-[15px] tracking-wide active:scale-95 transition shadow-xl shadow-[#C9A961]/30 flex items-center justify-center gap-2"
          data-testid="landing-cta-tour-pro"
        >
          <Play className="w-3.5 h-3.5" fill="currentColor" /> Watch the 2-minute tour
        </Link>
        <Link
          to="/tour"
          className="block w-full bg-transparent text-[#F0E6D2] rounded-full py-2 text-center font-display italic text-[13px] font-light tracking-wide active:scale-95 transition border border-white/[0.12]"
          data-testid="landing-cta-tour"
        >
          Quick 55-second peek instead
        </Link>
        <p className="text-center font-display italic text-[11px] text-[#A0A0A8] font-light">18+ · Real humans only</p>

        <p className="text-center text-[10px] text-[#6E6A60]" data-testid="landing-founder-credit">
          Founded by <span className="text-[#A0A0A8]">Givon J Johnson</span>
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 pt-0.5 text-[9px] uppercase tracking-[0.16em] text-[#6E6A60]">
          <Link to="/pricing" data-testid="landing-link-pricing" className="hover:text-[#F0E6D2] transition">Pricing</Link>
          <span>·</span>
          <Link to="/blog" data-testid="landing-link-blog" className="hover:text-[#F0E6D2] transition">Blog</Link>
          <span>·</span>
          <Link to="/faq" data-testid="landing-link-faq" className="hover:text-[#F0E6D2] transition">FAQ</Link>
          <span>·</span>
          <Link to="/terms" data-testid="landing-link-terms" className="hover:text-[#F0E6D2] transition">Terms</Link>
          <span>·</span>
          <Link to="/privacy" data-testid="landing-link-privacy" className="hover:text-[#F0E6D2] transition">Privacy</Link>
          <span>·</span>
          <Link to="/aup" data-testid="landing-link-aup" className="hover:text-[#F0E6D2] transition">Acceptable Use</Link>
          <span>·</span>
          <Link to="/child-safety" data-testid="landing-link-cs" className="hover:text-[#F0E6D2] transition">Child Safety</Link>
          <span>·</span>
          <Link to="/refund" data-testid="landing-link-refund" className="hover:text-[#F0E6D2] transition">Refunds</Link>
          <span>·</span>
          <Link to="/law-enforcement" data-testid="landing-link-le" className="hover:text-[#F0E6D2] transition">Law Enforcement</Link>
          <span>·</span>
          <a href="mailto:business@sparkdatin.com" className="hover:text-[#F0E6D2] transition">Contact</a>
        </div>
      </div>
    </div>
  );
}

function ElegantFeature({ icon, color, title, to, testid }) {
  const inner = (
    <div className="flex flex-col items-center gap-1 bg-[#1E1F23] border border-[#2A2A2F] rounded-lg py-1.5 active:scale-95 transition cursor-pointer">
      <div className="w-5 h-5 rounded-md flex items-center justify-center text-[#F0E6D2]" style={{ backgroundColor: color }}>{icon}</div>
      <span className="font-display italic text-[10px] text-[#D8CFC0] font-light leading-none">{title}</span>
    </div>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testid} className="block">
        {inner}
      </Link>
    );
  }
  return inner;
}
